import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { icon } from "../../../../config";
import { usePopup } from "../../../../helper/commonHooks";
import { getTriggeredTasks } from "../../../../shared/services/automationService";
import InstanceChart from "../../../Automation/AutomationDetail/InstanceChart";
import InstanceTaskTable from "../../../Automation/AutomationDetail/InstanceTaskTable";
import { getColumns, getTriggeredTasksColumns } from "../../../Automation/AutomationDetail/custom-cells";
import { getDateInFormat } from "../../../Automation/AutomationDetail/custom-cells";
import { handleCloseSidebar } from "../../../Tasks/tasks.common";
import "./TriggeredTasksPopup.scss";

/**
 * TriggeredTasksPopup Component
 *
 * Displays triggered tasks in a grid format with filtering options
 * Similar to the "Triggered Tasks" panel shown in the image
 * Uses the same popup structure as DescriptionPopup
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the popup is open
 * @param {Function} props.onClose - Function to close the popup
 * @param {string} props.parentTaskId - The parent task ID to fetch triggered tasks for
 * @author Muskan Thakur
 */
const TriggeredTasksPopup = ({ isOpen, onClose, parentTaskId }) => {
  const [triggeredTasks, setTriggeredTasks] = useState([]);
  const [automationCounts, setAutomationCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const { task } = useSelector((state) => state.taskSidebar);
  const { anchor } = usePopup();
  const dispatch = useDispatch();
  const history = useHistory();
  const { assigneeList } = useSelector((state) => state?.tasks?.defaultDetails);

  const fetchTriggeredTasks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getTriggeredTasks(parentTaskId);
      setTriggeredTasks(response.automationChildTasks);
      setAutomationCounts(response.automationCounts);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [parentTaskId]);

  // Fetch triggered tasks when popup opens
  useEffect(() => {
    if (isOpen && parentTaskId) {
      // Reset triggered tasks when popup opens
      setTriggeredTasks([]);
      setLoading(true);
      fetchTriggeredTasks();
    } else {
      // Reset when popup closes
      setTriggeredTasks([]);
      setLoading(false);
    }
  }, [isOpen, parentTaskId, fetchTriggeredTasks]);

  const handleAutomationClick = async () => {
    await handleCloseSidebar(dispatch, { isNewTask: false });
    if (automationCounts?.AutomationId) {
      history.push(`/automation/${task?.ProjectId}/${automationCounts?.AutomationId}`);
    }
  };

  return (
    <React.Fragment>
      <Popup
        show={isOpen}
        anchor={anchor?.current}
        className='dt-popup triggered-tasks-popup-container'
        popupClass='triggered-tasks-popup-child'
        animate={false}>
        <div className='triggered-tasks-popup-content' data-testid='triggered-tasks-popup-testid'>
          <div className='d-flex justify-content-between align-items-center p-2 border-bottom'>
            <div className='font-weight-bold'>Triggered Tasks</div>
            <span className='dt-popup-cancel-btn' onClick={onClose} title='Close Popup'>
              <Tooltip anchorElement='target' position='left' parentTitle='true'>
                {icon.CLOSE}
              </Tooltip>
            </span>
          </div>
          <div className='row mt-2 border-bottom mb-2'>
            <div className='col-12 col-md-5'>
              <div className='p-3'>
                <span className='font-weight-bold'>Reference : </span>
                <div className='mt-2'>
                  Automation :
                  <span className='link-text cursor-pointer' onClick={() => handleAutomationClick()}>
                    {automationCounts?.AutomationName}
                  </span>
                </div>
                <div className='mt-2'>
                  Trigger :
                  <span className='link-text cursor-pointer' onClick={() => handleAutomationClick()}>
                    {automationCounts?.TriggerName}
                  </span>
                </div>
                <div className='mt-2'>
                  Initiated On :<span>{getDateInFormat(automationCounts?.ParentCreatedDate)}</span>
                </div>
              </div>
            </div>
            <div className='col-12 col-md-1 d-flex align-items-center justify-content-center'>
              <div className='border-right h-100'></div>
            </div>
            <div className='col-12 col-md-6 p-3'>
              <InstanceChart instance={{ ...automationCounts, tasks: { length: automationCounts?.TotalTask } }} />
            </div>
          </div>
          <InstanceTaskTable columns={getColumns(user.id, assigneeList, dispatch)} data={triggeredTasks} height='400px' />
        </div>
      </Popup>
    </React.Fragment>
  );
};

export default TriggeredTasksPopup;
